import { RadioButtonWithLabelGroupWidget } from '@tableau/widgets';
import * as React from 'react';
import * as _ from 'underscore';

import { TypeUnsafeTranslation } from '~src/common/react/IReactTranslateService';
import { ISelectFilterDefinition } from '~src/content/services/FilterPanelService';

import { ClearFilterButton } from './ClearFilterButton';
import { IFilterControl, submitUrlQuery } from './FilterPanel';
import { filterComponent, filterWrapper } from './FilterPanelStyles';

export interface RadioButtonGroupFilterProps {
  readonly filterControl: IFilterControl;
  readonly shouldClear: boolean;
}

export function RadioButtonGroupFilter(props: RadioButtonGroupFilterProps) {
  const { filterControl } = props;
  const defaultValue = (filterControl.definition as ISelectFilterDefinition<any>).defaultValue;
  const options = (filterControl.definition as ISelectFilterDefinition<any>).options ?? [];
  const wrapperRef = React.useRef<HTMLDivElement>();

  const getIndexFromValue = (value: string) => {
    return _.findIndex(options, (option) => option.value === value);
  };

  const defaultIndex = getIndexFromValue(defaultValue);

  const [checkedIndex, setCheckedIndex] = React.useState<number>(getIndexFromValue(filterControl.value));

  React.useEffect(
    () => {
      const selectedValue = filterControl.value;
      setCheckedIndex(getIndexFromValue(selectedValue));
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterControl.value]
  ); // triggers on changing and clearing filters

  const handleChange = (value: string, index: number): void => {
    setCheckedIndex(index);

    const params = filterControl.definition.valueToParams(value);
    submitUrlQuery(params);
  };

  // Listen for clearing all filters
  React.useEffect(() => {
    if (props.shouldClear) {
      setCheckedIndex(defaultIndex);
    }
  }, [props.shouldClear, defaultIndex]);

  const clearFilter = () => {
    handleChange(defaultValue, defaultIndex);
  };


  return <div className={filterWrapper} ref={wrapperRef}>
    <RadioButtonWithLabelGroupWidget
      buttonList={((filterControl.definition as ISelectFilterDefinition<any>).options ?? []).map(option => ({
        label: TypeUnsafeTranslation.translate(option.translate),
        testId: 'option-' + option.value,
        value: option.value,
      }))}
      checkedIndex={checkedIndex}
      className={filterComponent}
      handleChange={handleChange}
      label={TypeUnsafeTranslation.translate(filterControl.definition.name)}
      testId={'radio-button-filter-' + filterControl.definition.name}
    />
    {checkedIndex !== defaultIndex &&
      <ClearFilterButton
        clearFilter={clearFilter}
        filterName={filterControl.definition.name}
        getElementToRefocus={() => wrapperRef.current?.querySelectorAll<HTMLElement>('input').item(defaultIndex)}
      />
    }
  </div>;
}
